import React from 'react'
import {AppState} from 'react-native'

import {syncContactToResend} from '#/lib/api/resend-contacts'
import {logger} from '#/logger'
import * as persisted from '#/state/persisted'
import {useSession} from '#/state/session'

const SIX_HOURS_MS = 6 * 60 * 60 * 1000 // 6 hours in milliseconds
const CHECK_INTERVAL_MS = 60 * 1000 // Check every minute if sync is needed

/**
 * Provider that handles periodic syncing of user contacts to Resend.
 * Syncs every 6 hours when the user is logged in and the app is active.
 */
export function ResendContactsSyncProvider({
  children,
}: React.PropsWithChildren<{}>) {
  const {currentAccount, hasSession} = useSession()

  const syncIfNeeded = React.useCallback(async () => {
    // Only sync if user is logged in and has an email
    if (!hasSession || !currentAccount?.email) {
      return
    }

    // Only sync when app is active
    if (AppState.currentState !== 'active') {
      return
    }

    // Check if 6 hours have passed since last sync
    const lastSync = persisted.get('lastResendContactSync')
    if (lastSync) {
      const lastSyncTime = new Date(lastSync).getTime()
      const now = Date.now()
      if (now - lastSyncTime < SIX_HOURS_MS) {
        // Not yet time to sync
        return
      }
    }

    logger.debug('Periodic Resend contact sync starting', {
      handle: currentAccount.handle,
    })

    const result = await syncContactToResend({
      email: currentAccount.email,
      handle: currentAccount.handle,
      did: currentAccount.did,
    })

    if (result.success) {
      // Update last sync time
      void persisted.write(
        'lastResendContactSync',
        new Date().toISOString(),
      )
      logger.debug('Periodic Resend contact sync completed', {
        handle: currentAccount.handle,
        updated: result.updated,
      })
    }
  }, [hasSession, currentAccount])

  // Set up periodic check
  React.useEffect(() => {
    if (!hasSession) {
      return
    }

    // Initial sync check on mount (after a small delay to not block startup)
    const initialTimeout = setTimeout(() => {
      void syncIfNeeded()
    }, 5000)

    // Set up interval to check if sync is needed
    const interval = setInterval(() => {
      void syncIfNeeded()
    }, CHECK_INTERVAL_MS)

    return () => {
      clearTimeout(initialTimeout)
      clearInterval(interval)
    }
  }, [hasSession, syncIfNeeded])

  // Also check when app comes to foreground
  React.useEffect(() => {
    const subscription = AppState.addEventListener('change', nextAppState => {
      if (nextAppState === 'active') {
        // Small delay to ensure app is fully active
        setTimeout(() => {
          void syncIfNeeded()
        }, 1000)
      }
    })

    return () => {
      subscription.remove()
    }
  }, [syncIfNeeded])

  return <>{children}</>
}
